import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import leaveService from "../../services/leaveService";
import { queryKeys } from "./queryKeys";

export const useLeaveManagementList = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.leaves.management(params),
    queryFn: async () => {
      const response = await leaveService.getLeaveManagementList(params);
      return response?.data ?? response;
    },
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateLeaveManagementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => leaveService.createLeaveManagementType(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
};

export const useUpdateLeaveManagementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) =>
      leaveService.updateLeaveManagementType(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.leaves.detail?.(variables.id) });
      }
    },
  });
};

export const useDeleteLeaveManagementType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => leaveService.deleteLeaveManagementType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
};

export const useUpdateLeaveManagementStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) =>
      leaveService.updateLeaveManagementStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
    },
  });
};

export const useLeaves = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.leaves.list(filters),
    queryFn: () => leaveService.getLeaves(filters),
    staleTime: 2 * 60 * 1000,
  });
};

export const useLeave = (id) => {
  return useQuery({
    queryKey: queryKeys.leaves.detail(id),
    queryFn: () => leaveService.getLeaveById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

export const useLeaveRequests = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.leaves.pending(filters),
    queryFn: () => leaveService.getLeaveRequests(filters),
    staleTime: 1 * 60 * 1000, // 1 minute for pending requests
  });
};

export const useMyLeaves = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.leaves.myLeaves(filters),
    queryFn: async () => {
      const response = await leaveService.getUserOwnLeaves(filters);
      return response?.data ?? response;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useStudentLeaves = (studentId, filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "student", studentId, filters],
    queryFn: () => leaveService.getStudentLeaves(studentId, filters),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useClassLeaves = (classId, filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "class", classId, filters],
    queryFn: () => leaveService.getClassLeaves(classId, filters),
    enabled: !!classId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useLeaveBalance = (userId) => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "balance", userId || "me"],
    queryFn: () => leaveService.getLeaveBalance(userId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useLeaveTypes = () => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "types"],
    queryFn: () => leaveService.getLeaveTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - leave types don't change often
  });
};

// Fetch leave-manage types (institute-specific, role filtered)
export const useLeaveManageTypes = () => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "manage-types"],
    queryFn: async () => {
      const response = await leaveService.getLeaveManagementTypes();
      return response?.data ?? response;
    },
    staleTime: 30 * 60 * 1000,
  });
};

export const useAllocations = () => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, 'allocations'],
    queryFn: () => leaveService.getAllocations().then(res => res?.data ?? res),
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => leaveService.createAllocation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.leaves.all, 'allocations'] });
    },
  });
};

export const useUpdateAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => leaveService.updateAllocation(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.leaves.all, 'allocations'] });
    },
  });
};

export const useDeleteAllocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => leaveService.deleteAllocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leaves.all });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.leaves.all, 'allocations'] });
    },
  });
};

export const useLeaveStatistics = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.leaves.all, "statistics", filters],
    queryFn: () => leaveService.getLeaveStatistics(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useApplyLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leaveData) => leaveService.applyLeave(leaveData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.myLeaves(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.lists(),
      });
    },
  });
};

export const useUpdateLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, leaveData }) =>
      leaveService.updateLeave(id, leaveData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.lists(),
      });
    },
  });
};

export const useCancelLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => leaveService.cancelLeave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.lists(),
      });
    },
  });
};

export const useApproveLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => {
      // Use new PATCH endpoint for approve
      // API expects: leaveId and action in body payload
      const requestBody = {
        leaveId: Number(id),
        action: "approve",
      };
      if (data?.remarks) {
        requestBody.remarks = data.remarks;
      }
      console.log('Approve leave request body:', requestBody);
      return leaveService.approveRejectLeave(requestBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.pending(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.leaves.all, "all"],
      });
    },
  });
};

export const useRejectLeave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => {
      // Use new PATCH endpoint for reject
      // API expects: leaveId and action in body payload
      const requestBody = {
        leaveId: Number(id),
        action: "reject",
        rejectionReason: String(data?.remarks || data?.rejectionReason || "").trim() || "Rejected by approver",
      };
      console.log('Reject leave request body:', requestBody);
      return leaveService.approveRejectLeave(requestBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.pending(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.leaves.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.leaves.all, "all"],
      });
    },
  });
};

export const useAllLeaves = (params = {}) => {
  console.log('useAllLeaves hook called with params:', params);
  console.log('useAllLeaves hook - params type:', typeof params, 'is object:', typeof params === 'object');
  
  const queryKey = [...queryKeys.leaves.all, "all", params];
  console.log('useAllLeaves queryKey:', queryKey);
  
  return useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      console.log('useAllLeaves queryFn: Making API call with params:', params);
      console.log('useAllLeaves queryFn: About to call leaveService.getAllLeaves');
      try {
        const response = await leaveService.getAllLeaves(params);
        console.log('useAllLeaves queryFn: Full axios response:', response);
        console.log('useAllLeaves queryFn: response.data:', response?.data);
        // Axios wraps the response in .data, so return response.data
        // The API response is in response.data, which should be { status: 200, message: "...", data: { leaves: [...] } }
        if (response?.data) {
          console.log('useAllLeaves queryFn: Returning response.data:', response.data);
          return response.data;
        }
        console.log('useAllLeaves queryFn: No response.data, returning full response:', response);
        return response;
      } catch (error) {
        console.error('useAllLeaves queryFn: API error:', error);
        throw error;
      }
    },
    enabled: true, // Explicitly enable the query
    staleTime: 0, // Set to 0 to always fetch fresh data
    gcTime: 0, // Set to 0 to not cache
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

